import React, { useCallback, useEffect, useState } from 'react'
import st from '../Deals.module.scss'
import { Draggable } from 'react-beautiful-dnd'
import DealsCard from '../DealsCard/DealsCard'
import { superAxiosRequest } from 'axiosApi'
import useOnScreen from '../useOnScreen'

const ColumnDeals = ({
  provided,
  filter,
  column,
  columns,
  dealsMap,
  sortOrder,
  setDealsMap,
  totalMap,
  setTotalMap,
  page,
  setPage,
  funnelId,
  addNewDealModal,
  translate,
  isLastColumn,
  selectedDeals,
  toggleDealSelection,
  onSelectAllInColumn,
}) => {
  const [hasMore, setHasMore] = useState(false)
  const [localTotal, setLocalTotal] = useState(
    totalMap?.[column.id]?.total ?? 0,
  )
  const [isAllSelected, setIsAllSelected] = useState(false)

  useEffect(() => {
    if (totalMap?.[column.id]?.total !== undefined) {
      setLocalTotal(totalMap[column.id].total)
    }
  }, [totalMap, column.id])

  useEffect(() => {
    if (dealsMap && dealsMap.length > 0) {
      const allLoadedSelected = dealsMap.every((deal) =>
        selectedDeals.includes(deal.id),
      )
      setIsAllSelected(allLoadedSelected)
    } else {
      setIsAllSelected(false)
    }
  }, [dealsMap, selectedDeals])

  const loadMore = useCallback(() => {
    setPage((page) => ({
      ...page,
      [column.id]: page[column.id] ? page[column.id] + 1 : 1,
    }))
  }, [column.id, setPage])

  const { measureRef, isIntersecting, observer } = useOnScreen()

  useEffect(() => {
    if (isIntersecting && hasMore) {
      loadMore()
      if (observer) {
        observer.disconnect()
      }
    }
  }, [isIntersecting, hasMore, loadMore, observer])

  const fetchDealData = () => {
    if (filter?.status_deal && filter?.status_deal !== column?.id) {
      return
    }
    const queryParams = {
      ...filter,
      funnel_id: funnelId,
      order_by: sortOrder,
      offset: (page[column.id] ?? 0) * 10,
      status_deal: column.id,
    }
    superAxiosRequest({
      link: 'integration/deals',
      method: 'get',
      crm: true,
      params: queryParams,
    })
      .then((response) => {
        setDealsMap((prev) => {
          const existingIds = new Set(
            prev[column.id]?.map((deal) => deal.id) || [],
          )
          const newDeals = response.data.result.filter(
            (deal) => !existingIds.has(deal.id),
          )

          return {
            ...prev,
            [column.id]: [...(prev[column.id] || []), ...newDeals],
          }
        })
        setTotalMap((prev) => ({
          ...prev,
          [column.id]: {
            total: response.data._meta?.count_items,
            budgetSum: response.data._meta?.total_price?.budget__sum,
          },
        }))
        setHasMore(response.data.result.length > 0)
      })
      .catch(() => {})
  }

  useEffect(() => {
    fetchDealData()
  }, [page[column.id], sortOrder]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectAllInColumn = () => {
    if (dealsMap && dealsMap.length > 0) {
      onSelectAllInColumn(column.id, dealsMap)
    }
  }

  const hasDeals = dealsMap && dealsMap.length > 0

  return (
    <div
      ref={provided.innerRef}
      {...provided.droppableProps}
      className={st.cardsColumn}
    >
      <div
        className={`${st.columnHeader}`}
        style={{
          borderBottom: `6px solid ${column?.color?.body_color !== undefined ? column?.color?.body_color : '#55b1f3'}`,
        }}
      >
        <div className={st.columnName}>{column.name}</div>
        <div className={st.columnHeaderRight}>
          <div className={st.countTask}>
            {localTotal} {translate.deals}
          </div>
          {hasDeals && (
            <button
              className={st.selectAllButton}
              onClick={handleSelectAllInColumn}
            >
              <svg
                width='14'
                height='14'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                {isAllSelected ? (
                  <path
                    d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'
                    fill='#4caf50'
                  />
                ) : (
                  <path d='M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z' fill='#666' />
                )}
              </svg>
              <span>{isAllSelected ? 'Отменить' : 'Выбрать всe'}</span>
            </button>
          )}
        </div>
      </div>
      {column.id === 2 && (
        <button className={st.taskQuikAdd} onClick={addNewDealModal}>
          {translate.quick_add}
        </button>
      )}

      {dealsMap?.map((deal, index) => (
        <Draggable
          key={`${deal.id}${index}`}
          draggableId={deal.id.toString()}
          index={index}
        >
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              className={snapshot.isDragging ? st.draggingDeal : st.deal}
            >
              {index === dealsMap?.length - 1 ? (
                <DealsCard
                  translate={translate}
                  {...deal}
                  has_tasks={deal.has_tasks}
                  funnelId={funnelId}
                  measureRef={measureRef}
                  isLastColumn={isLastColumn}
                  columnsLength={columns?.length}
                  isSelected={selectedDeals.includes(deal.id)}
                  onToggleSelection={() => toggleDealSelection(deal.id)}
                />
              ) : (
                <DealsCard
                  translate={translate}
                  columnsLength={columns?.length}
                  has_tasks={deal.has_tasks}
                  {...deal}
                  funnelId={funnelId}
                  isLastColumn={isLastColumn}
                  isSelected={selectedDeals.includes(deal.id)}
                  onToggleSelection={() => toggleDealSelection(deal.id)}
                />
              )}
            </div>
          )}
        </Draggable>
      ))}
      {provided.placeholder}
    </div>
  )
}

export default ColumnDeals
